/**
 * Model Management Service (Venice-only)
 *
 * Provides the app's selected models from `app_settings.llm_settings` (compact,
 * 4 keys). Models, capabilities and pricing are fetched dynamically from Venice
 * via modelFetchService — nothing model-specific is hardcoded here.
 */

import { supabase } from '../lib/supabaseClient';
import { getEffectiveDefaultModel } from './modelFetchService';

export interface AppModelSettings {
  textModel: string;
  fallbackTextModel: string;
  imageModel: string;
  fallbackImageModel: string;
}

class ModelService {
  private static instance: ModelService;
  private settings: AppModelSettings | null = null;
  private settingsPromise: Promise<AppModelSettings> | null = null;
  private readonly SETTINGS_KEY = 'llm_settings';

  private constructor() { }

  public static getInstance(): ModelService {
    if (!ModelService.instance) {
      ModelService.instance = new ModelService();
    }
    return ModelService.instance;
  }

  /** Resolve the current best defaults dynamically from Venice. */
  private async buildAppModelSettingsDynamically(): Promise<AppModelSettings> {
    const textModel = await getEffectiveDefaultModel('text');
    const imageModel = await getEffectiveDefaultModel('image');
    return { textModel, fallbackTextModel: textModel, imageModel, fallbackImageModel: imageModel };
  }

  public async getSettings(): Promise<AppModelSettings> {
    if (this.settings) return this.settings;
    if (this.settingsPromise) return this.settingsPromise;
    this.settingsPromise = this.fetchSettings();
    this.settings = await this.settingsPromise;
    return this.settings;
  }

  private async fetchSettings(): Promise<AppModelSettings> {
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', this.SETTINGS_KEY)
        .single();

      if (data?.value) {
        const saved = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
        const base = await this.buildAppModelSettingsDynamically();

        // Accept both the new compact keys and the legacy alias keys, but
        // collapse everything onto the compact shape. Provider is always Venice.
        const textModel = saved.textModel || saved.textGenerationModel || saved.studioGenerationModel || '';
        const imageModel = saved.imageModel || saved.imageGenerationModel || '';
        const fallbackTextModel = saved.fallbackTextModel || '';
        const fallbackImageModel = saved.fallbackImageModel || '';

        return {
          textModel: textModel || base.textModel,
          fallbackTextModel: fallbackTextModel || textModel || base.textModel,
          imageModel: imageModel || base.imageModel,
          fallbackImageModel: fallbackImageModel || imageModel || base.imageModel,
        };
      }

      return await this.buildAppModelSettingsDynamically();
    } catch {
      return this.buildAppModelSettingsDynamically();
    }
  }

  public async saveSettings(settings: AppModelSettings): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({
          key: this.SETTINGS_KEY,
          value: JSON.stringify(settings),
          updated_at: new Date().toISOString(),
        });

      if (error) return false;
      this.settings = settings;
      return true;
    } catch {
      return false;
    }
  }

  // ── Public accessors ───────────────────────────────────────────────────────

  public async getTextGenerationModel(_usePremium: boolean = false): Promise<string> {
    return (await this.getSettings()).textModel;
  }

  public async getImageGenerationModel(): Promise<string> {
    return (await this.getSettings()).imageModel;
  }

  public async getFallbackTextModel(): Promise<string> {
    const s = await this.getSettings();
    return s.fallbackTextModel || s.textModel;
  }

  public async getFallbackImageModel(): Promise<string> {
    const s = await this.getSettings();
    return s.fallbackImageModel || s.imageModel;
  }

  /**
   * Back-compat shim: map a feature name to its model id. All text features use
   * `textModel`; image features use `imageModel`. Provider is always Venice.
   */
  public async getModelForFeature(feature: string): Promise<string> {
    const s = await this.getSettings();
    return feature.includes('image') ? s.imageModel : s.textModel;
  }

  /** Provider is always Venice. */
  public async getProviderForFeature(_feature: string): Promise<'venice'> {
    return 'venice';
  }

  public async getSettingsForUI(): Promise<AppModelSettings> {
    return this.getSettings();
  }

  public clearCache(): void {
    this.settings = null;
    this.settingsPromise = null;
  }
}

export const modelService = ModelService.getInstance();

// Convenience re-exports (backward-compatible signatures).
export const getModelForFeature = (feature: string) => modelService.getModelForFeature(feature);
export const getProviderForFeature = (feature: string) => modelService.getProviderForFeature(feature);
export const getTextGenerationModel = () => modelService.getTextGenerationModel();
export const getImageGenerationModel = () => modelService.getImageGenerationModel();
export const getFallbackTextModel = () => modelService.getFallbackTextModel();
export const getFallbackImageModel = () => modelService.getFallbackImageModel();